import { Injectable } from '@angular/core';
import { Config } from './config';
import { EMPTY, Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DeviceTypeCode } from '../enums/device-type-code';
import { MONITOR_SERIES_TYPES } from '../models/monitor-series-types';
import { TritonLocationService } from './tritonLocation.service';
import { TritonConfiguration } from '../models/monitorsetting';
import { map } from 'rxjs/operators';
import { FlexDiagnosticWindow, FlexDiagnosticWindowRequest } from '../models/flex-diagnostic-window';
import { FlexMonitorStatusResponse } from '../models/flex-monitor-status';

export interface MonitorDiagnosticLogsRequest {
    startTime: Date;
    endTime: Date;
}

interface Reading {
    accelerometerRotation?: number | null;
    accelerometerSlope?: number | null;
    device: string;
    gain?: number | null;
    maximumSignal?: number | null;
    monitoringPoint: 1 | 2;
    quality?: number | null;
    reading?: number | null;
    sensor: string;
    temperature?: number | null;
}

interface Status {
    basicFaultStatus: number;
    basicProgramFlag: boolean;
    batteryVoltage: number;
    currentMonitorTime: Date;
    dataStorageCapability: number;
    eventsPendingStatus: number;
    firmwareVersion: string;
    localCommunicationsStatus: boolean;
    modelNumber: string;
    monitorTemperature: number;
    newestEntryTime: Date;
    numberOfAbnormalResets: number;
    oldestEntryTime: Date;
    powerStatus: number;
    pressureContainmentStatus: boolean;
    sampleRate: number;
}

export interface MonitorDiagnosticLog {
    user?: string;
    commandId?: string | null;
    modbusSensorId?: number | null;
    sensorSerialNumber?: string | null;
    readings: Reading[];
    status: Status;
    wirelessStatus?: WirelessStatus;
}

export interface WirelessStatus {
    signalStrength: number;
}

interface ReadingRequest {
    device: DeviceTypeCode;
    sensor: SensorType;
}
export interface MonitorDiagnosticRequest {
    customerID: number;
    locationID: number;
    readings: ReadingRequest[];
}

export interface FlexDiagnosticCommandResponse {
    commandId: string;
    modbusSensorId: number;
}

export enum SensorType {
    DEFAULT,
    ULTRASONIC,
    VELOCITY,
    PRESSURE,
    SURFACE_VELOCITY,
    TEMPERATURE,
    ACCELEROMETER,
}

@Injectable()
export class MonitorDiagnosticsService {
    constructor(private http: HttpClient, private tritonLocationService: TritonLocationService) {}

    public getDiagnosticLogs(cid: number, lid: number, body?: MonitorDiagnosticLogsRequest) {
        return this.http
            .post<MonitorDiagnosticLog[]>(
                `${Config.serviceUrl}${Config.urls.diagnosticLogs}?cid=${cid}&lid=${lid}`,
                body,
            )
            .pipe(
                map(
                    (result) => result,
                    () => EMPTY,
                ),
            );
    }

    public requestDiagnostics(body: MonitorDiagnosticRequest): Observable<FlexDiagnosticCommandResponse[] | null> {
        return this.http.post<FlexDiagnosticCommandResponse[] | null>(
            `${Config.serviceUrl}${Config.urls.diagnostics}`,
            body,
        );
    }

    public getFlexMonitorStatus(cid: number, lid: number): Observable<FlexMonitorStatusResponse> {
        const params = new HttpParams().set('cid', cid).set('lid', lid);
        return this.http.get<FlexMonitorStatusResponse>(Config.urls.flexMonitorStatus, { params });
    }

    public getDiagnosticWindowFlex(cid: number, lid: number): Observable<FlexDiagnosticWindow> {
        return this.http.get<FlexDiagnosticWindow>(
            `${Config.serviceUrl}${Config.urls.diagnosticWindowFlex}?cid=${cid}&lid=${lid}`,
        );
    }

    public updateDiagnosticWindowFlex(cid: number, lid: number, body: FlexDiagnosticWindowRequest): Observable<void> {
        return this.http.post<void>(
            `${Config.serviceUrl}${Config.urls.diagnosticWindowFlex}?cid=${cid}&lid=${lid}`,
            body,
        );
    }

    public deleteDiagnosticWindowFlex(cid: number, lid: number): Observable<void> {
        return this.http.delete<void>(
            `${Config.serviceUrl}${Config.urls.diagnosticWindowFlex}?cid=${cid}&lid=${lid}`,
        );
    }

    public getDiagnosticsDetails(
        monitor: MONITOR_SERIES_TYPES,
        locationId: number,
        customerId: number,
    ): Observable<{ showDiagnostics: boolean; deviceTypes: DeviceTypeCode[] }> {
        if (!monitor) {
            return of({
                showDiagnostics: false,
                deviceTypes: [],
            });
        }

        switch (monitor.toLowerCase()) {
            case MONITOR_SERIES_TYPES.TRITON_PLUS.toLowerCase():
                return this.tritonLocationService.getTritonMonitorConfiguration(locationId, customerId).pipe(
                    map((tritonResult: TritonConfiguration) => {
                        if (tritonResult) {
                            return {
                                showDiagnostics: true,
                                deviceTypes: tritonResult.devices.map((device) => DeviceTypeCode.getDeviceTypeCode(device.type)),
                            };
                        } else {
                            return {
                                showDiagnostics: false,
                                deviceTypes: [],
                            };
                        }
                    }),
                );
            case MONITOR_SERIES_TYPES.ECHO.toLowerCase():
                return of({
                    showDiagnostics: true,
                    deviceTypes: [DeviceTypeCode.LongRangeDepth1],
                });
            case MONITOR_SERIES_TYPES.RAIN_ALERT_III.toLowerCase():
                return of({
                    showDiagnostics: true,
                    deviceTypes: [DeviceTypeCode.EchoRain],
                });
            case MONITOR_SERIES_TYPES.FLEX.toLowerCase():
                return of({
                    showDiagnostics: true,
                    deviceTypes: [DeviceTypeCode.EchoRain],
                });
            default:
                // don't show location diagnostics
                return of({
                    showDiagnostics: false,
                    deviceTypes: [],
                });
        }
    }
}
